import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import './Courses.css';

const sortOptions = [
  { label: 'Newest', value: 'newest' },
  { label: 'Most lessons', value: 'lessons' },
  { label: 'Price: Low to High', value: 'price-asc' },
  { label: 'Price: High to Low', value: 'price-desc' },
];

const Courses = () => {
  const { t } = useTranslation();

  const levelFilters = [
    { label: t('courses.allLevels'), value: 'all' },
    { label: t('courses.beginner'), value: 'beginner' },
    { label: t('courses.intermediate'), value: 'intermediate' },
    { label: t('courses.advanced'), value: 'advanced' },
  ];

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [levelFilter, setLevelFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [coursesRes, categoriesRes] = await Promise.all([
          api.get('/courses'),
          api.get('/categories'),
        ]);

        if (isMounted) {
          setCourses(coursesRes.data);
          setCategories(categoriesRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const categoryCounts = useMemo(() => {
    return courses.reduce((acc, course) => {
      if (!course.category_id) return acc;
      const key = String(course.category_id);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [courses]);

  const filteredCourses = useMemo(() => {
    let filtered = [...courses];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        (course) => String(course.category_id) === selectedCategory
      );
    }

    if (levelFilter !== 'all') {
      filtered = filtered.filter(
        (course) => (course.level || 'beginner').toLowerCase() === levelFilter
      );
    }

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((course) =>
        [
          course.title,
          course.short_description,
          course.description,
          course.category_name,
          course.instructor_name,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      );
    }

    return filtered.sort((a, b) => sortCourses(a, b, sortBy));
  }, [courses, selectedCategory, levelFilter, searchTerm, sortBy]);

  const stats = useMemo(() => {
    const totalHours = filteredCourses.reduce(
      (sum, course) => sum + Number(course.duration_hours || 0),
      0
    );
    const totalLessons = filteredCourses.reduce(
      (sum, course) => sum + Number(course.video_count || 0),
      0
    );
    const instructors = filteredCourses.reduce((set, course) => {
      if (course.instructor_name) {
        set.add(course.instructor_name);
      }
      return set;
    }, new Set());

    return {
      totalHours,
      totalLessons,
      instructors: instructors.size,
    };
  }, [filteredCourses]);

  const featuredCategory = useMemo(() => {
    if (selectedCategory !== 'all') {
      const selected = categories.find(
        (cat) => String(cat.id) === selectedCategory
      );
      if (selected) {
        return {
          ...selected,
          count: filteredCourses.length,
        };
      }
    }

    if (!categories.length) {
      return null;
    }

    return [...categories]
      .map((category) => ({
        ...category,
        count: categoryCounts[String(category.id)] || 0,
      }))
      .sort((a, b) => b.count - a.count)[0];
  }, [categories, categoryCounts, filteredCourses.length, selectedCategory]);

  const selectedCategoryName =
    selectedCategory === 'all'
      ? t('courses.allCategories')
      : categories.find((cat) => String(cat.id) === selectedCategory)?.name ||
        t('courses.allCategories');

  const resultsSummary =
    filteredCourses.length === 0
      ? 'No courses match your filters yet'
      : `Showing ${filteredCourses.length} course${
          filteredCourses.length === 1 ? '' : 's'
        } • ${stats.totalLessons} lesson${
          stats.totalLessons === 1 ? '' : 's'
        } in ${selectedCategoryName.toLowerCase()}`;

  const hasActiveFilters =
    selectedCategory !== 'all' || levelFilter !== 'all' || searchTerm.trim();

  const resetFilters = () => {
    setSelectedCategory('all');
    setLevelFilter('all');
    setSearchTerm('');
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="page page-courses">
      <div className="courses-container">
        <section className="courses-hero">
          <div className="hero-content">
            <p className="eyebrow">{t('courses.heroEyebrow')}</p>
            <h1>{t('courses.heroTitle')}</h1>
            <p className="hero-subtitle">
              {t('courses.heroSubtitle')}
              {` `}
              {selectedCategoryName} currently features{' '}
              <strong>{filteredCourses.length}</strong> active course
              {filteredCourses.length === 1 ? '' : 's'}.
            </p>

            <div className="hero-search">
              <div className="search-input">
                <SearchIcon />
                <input
                  type="text"
                  placeholder={t('courses.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="clear-search"
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <span className="results-meta">{resultsSummary}</span>
            </div>

            <div className="hero-stats">
              <div className="stat-card">
                <span className="stat-value">{filteredCourses.length}</span>
                <span className="stat-label">{t('courses.coursesLabel')}</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.totalLessons}</span>
                <span className="stat-label">{t('courses.lessonsLabel')}</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {stats.totalHours > 0
                    ? `${stats.totalHours}h`
                    : t('courses.selfPaced')}
                </span>
                <span className="stat-label">
                  {stats.totalHours > 0 ? t('courses.guidedHours') : t('courses.flexibleLearning')}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {stats.instructors || (filteredCourses.length ? 1 : 0)}
                </span>
                <span className="stat-label">{t('courses.instructorsLabel')}</span>
              </div>
            </div>
          </div>

          {featuredCategory && (
            <div className="hero-sidecard">
              <p className="side-label">
                {selectedCategory === 'all'
                  ? t('courses.popularTrack')
                  : t('courses.focusedCollection')}
              </p>
              <h3>{featuredCategory.name}</h3>
              <p className="sidecard-description">
                {featuredCategory.count > 0
                  ? `${featuredCategory.count} curated course${
                      featuredCategory.count === 1 ? '' : 's'
                    } waiting for you.`
                  : t('courses.newCategoryLaunching')}
              </p>
              <ul className="sidecard-points">
                <li>{t('courses.structuredOutcomes')}</li>
                <li>{t('courses.instructorSupport')}</li>
                <li>{t('courses.certificateReady')}</li>
              </ul>
              {selectedCategory === 'all' ? (
                <button
                  type="button"
                  className="hero-cta"
                  onClick={() =>
                    setSelectedCategory(String(featuredCategory.id))
                  }
                >
                  {t('courses.explore')} {featuredCategory.name}
                </button>
              ) : (
                <button
                  type="button"
                  className="hero-cta ghost"
                  onClick={() => setSelectedCategory('all')}
                >
                  {t('courses.resetCategory')}
                </button>
              )}
            </div>
          )}
        </section>

        <section className="courses-filters">
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="sort-select">{t('courses.sortBy')}</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group level-group">
              <label>{t('courses.level')}</label>
              <div className="pill-group">
                {levelFilters.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    className={`pill ${
                      levelFilter === level.value ? 'active' : ''
                    }`}
                    onClick={() => setLevelFilter(level.value)}
                  >
                    {level.label}
                  </button>
                ))}
              </div>
            </div>

            {hasActiveFilters && (
              <button type="button" className="link-button" onClick={resetFilters}>
                {t('courses.clearFilters')}
              </button>
            )}
          </div>

          <div
            className="category-pills"
            role="group"
            aria-label="Filter courses by category"
          >
            <button
              type="button"
              className={`pill ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              {t('courses.allCategories')}
              <span className="pill-count">{courses.length}</span>
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`pill ${
                  selectedCategory === String(category.id) ? 'active' : ''
                }`}
                onClick={() => setSelectedCategory(String(category.id))}
              >
                {category.name}
                {categoryCounts[String(category.id)] ? (
                  <span className="pill-count">
                    {categoryCounts[String(category.id)]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </section>

        {filteredCourses.length === 0 ? (
          <div className="empty-state elevated">
            <h3>{t('courses.noCoursesFound')}</h3>
            <p>
              {t('courses.tryAdjusting')}
            </p>
            <button type="button" className="btn-primary" onClick={resetFilters}>
              {t('courses.resetFilters')}
            </button>
          </div>
        ) : (
          <div className="courses-grid">
            {filteredCourses.map((course) => {
              const price = formatPrice(course);
              const description =
                course.short_description || course.description || '';

              return (
                <article key={course.id} className="course-card">
                  <div className="course-thumb">
                    {course.thumbnail_url ? (
                      <img src={course.thumbnail_url} alt={course.title} />
                    ) : (
                      <div className="thumb-placeholder">
                        {getInitials(course.title)}
                      </div>
                    )}
                    <span className="course-level">
                      {formatLevel(course.level)}
                    </span>
                  </div>

                  <div className="course-body">
                    <div className="course-top">
                      <span className="course-category">
                        {course.category_name || t('courses.general')}
                      </span>
                      {course.is_featured && (
                        <span className="course-badge">Featured</span>
                      )}
                      {course.requires_subscription && (
                        <span className="course-badge" style={{ background: '#7c3aed' }} aria-label="Requires subscription">
                          Subscription
                        </span>
                      )}
                    </div>
                    <h3>
                      <Link to={`/courses/${course.id}`}>{course.title}</Link>
                    </h3>
                    <p className="course-description">{description}</p>

                    <div className="course-meta">
                      <span>{formatDuration(course)}</span>
                      <span>{Number(course.video_count || 0)} lessons</span>
                      <span>
                        {course.instructor_name || 'Expert instructor'}
                      </span>
                    </div>
                  </div>

                  <div className="course-footer">
                    <div className="course-price">
                      <span className="price-current">{price.primary}</span>
                      {price.secondary && (
                        <span className="price-original">{price.secondary}</span>
                      )}
                    </div>
                    <Link to={`/courses/${course.id}`} className="course-link">
                      View details
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const sortCourses = (a, b, sortBy) => {
  const getPriceValue = (course) =>
    Number(course.sale_price || course.price || 0);

  switch (sortBy) {
    case 'price-asc':
      return getPriceValue(a) - getPriceValue(b);
    case 'price-desc':
      return getPriceValue(b) - getPriceValue(a);
    case 'lessons':
      return Number(b.video_count || 0) - Number(a.video_count || 0);
    case 'newest':
    default:
      return (
        new Date(b.created_at || b.updated_at || 0) -
        new Date(a.created_at || a.updated_at || 0)
      );
  }
};

const formatLevel = (level) => {
  if (!level) return 'Beginner';
  const normalized = level.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatDuration = (course) => {
  const hours = Number(course.duration_hours || 0);
  if (hours > 0) {
    return `${hours} hr${hours > 1 ? 's' : ''}`;
  }

  const lessons = Number(course.video_count || 0);
  if (lessons > 0) {
    return `${lessons} lesson${lessons > 1 ? 's' : ''}`;
  }

  return 'Self-paced';
};

const formatPrice = (course) => {
  if (course.is_free) {
    return { primary: 'Free' };
  }

  const price = Number(course.price || 0);
  const salePrice = Number(course.sale_price || 0);

  if (salePrice > 0 && salePrice < price) {
    return {
      primary: `$${salePrice.toFixed(2)}`,
      secondary: `$${price.toFixed(2)}`,
    };
  }

  if (price === 0) {
    return { primary: 'Free' };
  }

  return {
    primary: `$${price.toFixed(2)}`,
  };
};

const getInitials = (value = '') => {
  return value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

const SearchIcon = () => (
  <svg
    className="search-icon"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 21L16.65 16.65"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default Courses;
